import { useMemo } from 'react';
import { format, parse } from 'date-fns';
import { useSelector } from 'react-redux';
import {
  selectMeteorologyFieldsModelPerformancePeriodType,
  selectMeteorologyFieldsModelPerformanceVariable,
  selectMeteorologyFieldsModelPerformanceAnalysisType,
  selectMeteorologyFieldsModelPerformancePentad,
  selectMeteorologyFieldsModelPerformanceRounds,
  selectMeteorologyFieldsModelPerformanceAnalysisPeriod,
} from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import useOptions from './useOptions';

function formatRoundDate(round: string): string {
  try {
    return format(parse(round, 'yyyy-MM-dd', new Date()), 'dd/MM/yy');
  } catch {
    return round;
  }
}

export interface MapTitleResult {
  /** "Modelo de menor viés por bacia" ou "por região" */
  title: string;
  /** "{Variável} para a pêntada X | Rodadas avaliadas entre DD/MM/AA e DD/MM/AA" */
  subtitle: string;
  /** Título + " - " + subtítulo (legenda da foto baixada e texto quando sem dados). */
  placeholder: string;
}

export default function useMapTitle(): MapTitleResult {
  const periodType = useSelector(selectMeteorologyFieldsModelPerformancePeriodType);
  const variable = useSelector(selectMeteorologyFieldsModelPerformanceVariable);
  const analysisType = useSelector(selectMeteorologyFieldsModelPerformanceAnalysisType);
  const pentad = useSelector(selectMeteorologyFieldsModelPerformancePentad);
  const rounds = useSelector(selectMeteorologyFieldsModelPerformanceRounds);
  const analysisPeriod = useSelector(selectMeteorologyFieldsModelPerformanceAnalysisPeriod);
  const { variables, pentads, analysisTypes } = useOptions();

  return useMemo((): MapTitleResult => {
    // Título responde apenas a analysisTypes (reset ao mudar filtros)
    const tipoLabel = analysisTypes?.length
      ? (analysisTypes.find((a) => a.id === analysisType)?.label ?? '')
      : '';
    const isBacia = tipoLabel === 'Bacia' || tipoLabel === 'Bacias Hidrográficas';
    const isRegiao = tipoLabel === 'Região' || tipoLabel === 'Regiões';
    let title = 'Modelo de menor RMSE';
    if (isBacia) {
      title = 'Modelo de menor RMSE por bacia';
    } else if (isRegiao) {
      title = 'Modelo de menor RMSE por região';
    }

    const variableLabel = variable && variables?.length
      ? (variables.find((v) => v.id === variable)?.label ?? '')
      : '';
    const pentadLabel = pentad && pentads?.length
      ? (pentads.find((p) => p.id === pentad)?.label ?? '')
      : '';

    // Data mínima e máxima: pré-definido = rodadas; livre = período de análise
    let firstDate = '';
    let lastDate = '';
    if (periodType === 'preset' && rounds && rounds.length > 0) {
      const parsed = rounds.map((r) => {
        try {
          return { raw: r, time: parse(r, 'yyyy-MM-dd', new Date()).getTime() };
        } catch {
          return { raw: r, time: 0 };
        }
      });
      const min = parsed.reduce((a, b) => (a.time <= b.time ? a : b));
      const max = parsed.reduce((a, b) => (a.time >= b.time ? a : b));
      firstDate = formatRoundDate(min.raw);
      lastDate = formatRoundDate(max.raw);
    } else if (periodType === 'free' && analysisPeriod?.start && analysisPeriod?.end) {
      try {
        firstDate = format(parse(analysisPeriod.start, 'yyyy-MM-dd', new Date()), 'dd/MM/yy');
        lastDate = format(parse(analysisPeriod.end, 'yyyy-MM-dd', new Date()), 'dd/MM/yy');
      } catch {
        // ignora erro de parsing
      }
    }
    const dateRangeStr = firstDate && lastDate ? `Rodadas avaliadas entre ${firstDate} e ${lastDate}` : '';

    const subtitleParts: string[] = [];
    if (variableLabel) {
      if (periodType === 'free') {
        subtitleParts.push(`${variableLabel} para periodo livre`);
      } else if (pentadLabel) {
        subtitleParts.push(`${variableLabel} para a pêntada ${pentadLabel}`);
      } else {
        subtitleParts.push(variableLabel);
      }
    }
    if (dateRangeStr) {
      subtitleParts.push(dateRangeStr);
    }
    const subtitle = subtitleParts.join(' | ') || '';

    const placeholder = subtitle ? `${title} - ${subtitle}` : title;

    return { title, subtitle, placeholder };
  }, [
    periodType,
    variable,
    analysisType,
    pentad,
    rounds,
    analysisPeriod,
    variables,
    pentads,
    analysisTypes,
  ]);
}
